'use client';

/* eslint-disable @next/next/no-img-element */

import { createPortal } from 'react-dom';
import { RegisteredCurtain } from './RegisteredCurtain';
import { WhereSheet } from './WhereSheet';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarPlus,
  Check,
  ChevronLeft,
  Church,
  ChevronRight,
  Clock,
  Copy,
  ExternalLink,
  Info,
  MapPin,
  MessageCircleQuestion,
  Share,
  Ticket,
  Users,
  X,
} from 'lucide-react';
import type {
  EventDetailsResponse,
  EventMeResponse,
  InvoiceResponse,
  SheetDress,
} from '@/lib/types';
import {
  addPerson,
  bringBackPerson,
  fetchFormerMembers,
  type FormerMemberLite,
  fetchEventDetails,
  fetchEventMe,
  fetchInvoice,
  is404,
  saveGender,
  saveRelationship,
} from '@/lib/api';
import type { LearnedRelationship } from '@/lib/relationships';
import { onMpTokenChange, signInAndReturn } from '@/lib/campus';
import { MP_SIGN_IN_NEEDED } from '@church/nextjs-ui/website/mp-session';
import { ExperienceHost, experienceKeyFor, usePreview } from '@/experiences/ExperienceHost';
import { EXPERIENCES, experienceHint, rememberExperience } from '@/experiences/registry';
import { dateParts, fmtDay, fmtMoney, fmtTime, sameDay } from '@/lib/format';
import { askingPriceFor, waysInSummary } from '@/lib/pricing';
import { pageTitleFor, prettyUrlFor } from '@/lib/prettyUrl';
import { accentTokens, isHexColor } from '@/lib/accent';
import { chromeVarsFor, paintChrome } from '@/lib/chrome';
import { paintScrollbar } from '@/lib/scrollbar';
import {
  anyClips,
  bleedFor,
  headroomFor,
  overflowChain,
  riseFor,
  topBoxes,
  topFixedBottom,
  useIsomorphicLayoutEffect,
} from '@/lib/bleed';
import { usePortalContainer } from '@church/nextjs-ui/bottom-sheet';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import { WoodsideLogo } from '@church/nextjs-ui/website';
import { Agenda } from './Agenda';
import { ContactCard } from './ContactCard';
import { InvoiceCard } from './InvoiceCard';
import { OtherDates, SeriesNote } from './OtherDates';
import { PayDockHost } from './PayDock';
import { RegisterDock } from './RegisterDock';
import { SignUp, guestReady, type GuestDraft } from './SignUp';
import { AddPerson } from './AddPerson';
import { needsParent } from '@/lib/guests';
import { RegistrationSheet } from './RegistrationSheet';
import { FinishSheet } from './FinishSheet';

export interface EventDetailsProps {
  apiBaseUrl: string;
  /** From `?id=`; null when the page was reached by invoice or token alone. */
  eventId: number | null;
  /**
   * The event's name in the URL, when the page was reached by one.
   *
   * Only the first call needs it: the details response carries the numeric id,
   * and everything after — the household, registering, paying — uses that. So
   * a slug is a way in, not a second identifier to thread through.
   */
  eventSlug?: string | null;
  /** From `?invoice=` or the invoiceId claim in OLG's `?token=`. */
  invoiceGuid: string | null;
  /** OLG's return said the payment went through. */
  claimedSuccess: boolean;
  /** Where "All events" goes (stock `returnurl`). */
  finderUrl?: string;
  /** This page, for links to other dates: `…/events/details/?id=`. */
  detailsUrl?: string;
}

const DEFAULT_FINDER_URL = 'https://woodsidebible.org/events/';
const DEFAULT_DETAILS_URL = 'https://woodsidebible.org/events/details/?id=';

type Status = 'loading' | 'ready' | 'missing' | 'error';

/** The current page with no query string: where OLG returns to and what the share link is built on. */
function bareUrl(): string {
  const u = new URL(window.location.href);
  u.search = '';
  u.hash = '';
  return u.toString();
}

/**
 * One event page: the event, where registration stands, who in your
 * household is going, the sheet to register, and the state of any invoice on
 * it. Replaces `<mpp-event-details>`, `<mpp-checkout>` and the trip through
 * `/pay` with one experience on one URL.
 */
export function EventDetails({
  apiBaseUrl,
  eventId: eventIdProp,
  eventSlug = null,
  invoiceGuid: invoiceGuidProp,
  claimedSuccess,
  finderUrl = DEFAULT_FINDER_URL,
  detailsUrl = DEFAULT_DETAILS_URL,
}: EventDetailsProps) {
  const [eventId, setEventId] = useState<number | null>(eventIdProp);
  const [invoiceGuid, setInvoiceGuid] = useState<string | null>(invoiceGuidProp);
  /**
   * `?finish=1` beside `?invoice=`: somebody registered at the door, by
   * staff, with the questions left for them. The finish sheet opens on its
   * own; closing it leaves the ordinary page with the invoice card.
   */
  const [finishOpen, setFinishOpen] = useState(
    () =>
      Boolean(invoiceGuidProp) &&
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('finish') === '1'
  );
  const [details, setDetails] = useState<EventDetailsResponse | null>(null);
  const [me, setMe] = useState<EventMeResponse | null>(null);
  /** The page's own element, for the accent's custom properties. */
  const rootRef = useRef<HTMLElement>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [sheetOpen, setSheetOpen] = useState(false);
  /**
   * Whether the sheet was handed a selection.
   *
   * From the panel it is: ticking people IS the first step, so the sheet
   * starts on the next question. From the bar at the bottom of a phone it is
   * not, and the sheet asks who is coming itself — the step it has always had
   * and never reached. Keyed on the sheet so switching entry starts cleanly
   * rather than resuming a flow that began the other way.
   */
  const [sheetSeeded, setSheetSeeded] = useState(true);
  const openSheet = (seeded: boolean) => {
    setSheetSeeded(seeded);
    setSheetOpen(true);
  };
  /** The registration panel, so the bar knows when it has been reached. */
  const panelRef = useRef<HTMLElement>(null);
  /** An invoice opened from somebody's row: the whole receipt, everyone on it. */
  const [openInvoice, setOpenInvoice] = useState<string | null>(null);
  // Who the viewer has ticked in the panel, and anyone they typed in who is
  // not in the household. The sheet starts from both.
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [guests, setGuests] = useState<GuestDraft[]>([]);
  /**
   * The draft whose sheet is open. Adding somebody is an interview, not four
   * boxes at the bottom of a 22rem column, so the questions live in a sheet
   * and this is which person that sheet is about.
   */
  const [addingGuest, setAddingGuest] = useState<string | null>(null);
  // Archived former members of the viewer's household, read the first time
  // the add-person sheet opens, so a returning Guest Child is added back
  // rather than created twice.
  const [formerMembers, setFormerMembers] = useState<FormerMemberLite[] | null>(null);
  const [pageUrl, setPageUrl] = useState('');
  // OLG's return, this visit: from the URL on a full-page return, or from the popup's report.
  const [claimed, setClaimed] = useState(claimedSuccess);

  useEffect(() => setPageUrl(bareUrl()), []);

  const toggleChosen = (contactId: number) => {
    setChosen((was) => {
      const next = new Set(was);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  };

  const addGuest = () => {
    const key = `g${Date.now()}`;
    setGuests((was) => [
      ...was,
      {
        key,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        relationChoice: null,
        relationAsked: false,
        relationshipId: null,
        gender: null,
        addToHousehold: false,
        whereAnswered: false,
        dateOfBirth: '',
        nickname: '',
        middleName: '',
        positionId: null,
        maritalStatusId: null,
        shirtSizeId: null,
        workPhone: '',
      },
    ]);
    setAddingGuest(key);
  };

  /**
   * Write somebody down, and fold back whatever MP made of them.
   *
   * Awaited, unlike the relationship write below: the person is standing in
   * front of a sheet waiting to tick this name, so the answer decides what
   * they see next. An added person is fetched back with everybody else and
   * ticked; a pending one stays on the list as a draft that says it is
   * waiting, because the server will not hand back anybody unconfirmed.
   */
  useEffect(() => {
    if (!addingGuest || !eventId || formerMembers !== null) return;
    const controller = new AbortController();
    fetchFormerMembers(apiBaseUrl, eventId, controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setFormerMembers(list);
      })
      // An extra: without it the sheet simply adds as before.
      .catch(() => {
        if (!controller.signal.aborted) setFormerMembers([]);
      });
    return () => controller.abort();
  }, [addingGuest, eventId, apiBaseUrl, formerMembers]);

  /** Add a former member back in place of the draft being typed. */
  const bringBackFormer = async (contactId: number) => {
    if (!eventId || !addingGuest) return;
    const draftKey = addingGuest;
    await bringBackPerson(apiBaseUrl, eventId, contactId);
    setAddingGuest(null);
    setGuests((was) => was.filter((g) => g.key !== draftKey));
    setFormerMembers((list) => (list ?? []).filter((f) => f.contactId !== contactId));
    setChosen((was) => new Set(was).add(contactId));
    refreshMe();
  };

  const saveNewPerson = async (draft: GuestDraft) => {
    if (!eventId) return;
    const result = await addPerson(apiBaseUrl, eventId, {
      firstName: draft.firstName,
      lastName: draft.lastName,
      nickname: draft.nickname || null,
      middleName: draft.middleName || null,
      relationshipId: draft.relationshipId,
      gender: draft.gender,
      addToHousehold: draft.addToHousehold,
      dateOfBirth: draft.dateOfBirth || null,
      email: draft.email || null,
      phone: draft.phone || null,
      workPhone: draft.workPhone || null,
      positionId: draft.positionId,
      maritalStatusId: draft.maritalStatusId,
      shirtSizeId: draft.shirtSizeId,
      parent:
        needsParent(draft) && draft.parent?.role && draft.parent.role !== 'unknown'
          ? {
              firstName: draft.parent.firstName,
              lastName: draft.parent.lastName,
              phone: draft.parent.phone,
              email: draft.parent.email || null,
              role: draft.parent.role,
            }
          : null,
    });

    // Either way the sheet is done: the answer is now on the list behind it.
    setAddingGuest(null);
    if (result.outcome === 'added') {
      // They are a person now, so the draft has nothing left to hold.
      setGuests((was) => was.filter((g) => g.key !== draft.key));
      setChosen((was) => new Set(was).add(result.contactId));
      refreshMe();
      return;
    }
    setGuests((was) =>
      was.map((g) => (g.key === draft.key ? { ...g, saved: 'pending' as const } : g))
    );
  };

  // Reached by invoice alone (OLG's return, a shared link): learn the event from it.
  useEffect(() => {
    if (eventId || !invoiceGuid) return;
    const controller = new AbortController();
    fetchInvoice(apiBaseUrl, invoiceGuid, claimedSuccess, controller.signal)
      .then((r) => {
        if (r.event) setEventId(r.event.id);
        else setStatus('ready');
      })
      .catch(() => setStatus('ready'));
    return () => controller.abort();
  }, [apiBaseUrl, eventId, invoiceGuid, claimedSuccess]);

  useEffect(() => {
    // Whichever we were given. A slug only gets us through the door; the
    // response tells us the id, and everything after this uses that.
    const ref = eventId ?? eventSlug;
    if (!ref) {
      if (!invoiceGuid) setStatus('missing');
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    fetchEventDetails(apiBaseUrl, ref, controller.signal, bareUrl())
      .then((d) => {
        setDetails(d);
        if (!eventId && d.event?.id) setEventId(d.event.id);
        setStatus('ready');
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (is404(err)) setStatus('missing');
        else {
          console.error('[event-details] failed to load:', err);
          setStatus('error');
        }
      });
    return () => controller.abort();
  }, [apiBaseUrl, eventId, eventSlug]);

  /**
   * THE ADDRESS BECOMES THE ONE WORTH SENDING.
   *
   * A link out of MP, a calendar file or the finder lands on
   * `…/events/details/?id=337489`; the same page also answers to
   * `…/events/flannel-fest/`, which is what staff put on a flyer and what
   * anybody would actually send a friend. The event's answer carries its
   * slug, so once it has arrived the ugly address quietly becomes the pretty
   * one — replaced, not pushed, since Back should leave the page rather than
   * appear to do nothing.
   *
   * `pageUrl` moves with it, so the share sheet and OLG's return trip both
   * use the new address rather than the one nobody should see.
   */
  useEffect(() => {
    const slug = details?.event?.slug;
    if (!slug) return;
    const pretty = prettyUrlFor(slug, finderUrl, window.location.href);
    if (!pretty) return;
    window.history.replaceState(window.history.state, '', pretty);
    setPageUrl(bareUrl());
  }, [details, finderUrl]);

  /**
   * AND THE TAB SAYS WHICH EVENT IT IS.
   *
   * One WordPress template serves every event, so every one of them was
   * called "Event" in the tab strip, in the history, and in whatever a
   * person bookmarked. The event's own name goes in front of the site's,
   * which is kept rather than assumed — see `pageTitleFor`.
   *
   * Put back on the way out. The page is a full load on the website so it
   * would not matter there, but leaving a global changed is how a widget
   * becomes something nobody can host twice.
   */
  useEffect(() => {
    const title = details?.event?.title;
    if (!title) return;
    const next = pageTitleFor(title, document.title);
    if (!next) return;
    const before = document.title;
    document.title = next;
    return () => {
      document.title = before;
    };
  }, [details]);

  const refreshMe = useCallback(() => {
    if (!eventId) return;
    void fetchEventMe(apiBaseUrl, eventId).then(setMe);
  }, [apiBaseUrl, eventId]);
  useEffect(() => {
    refreshMe();
    return onMpTokenChange(() => refreshMe());
  }, [refreshMe]);

  // A save found the sign-in had run out and MP would not renew it. Open the
  // sign-in rather than an error; what they filled in stays on the page.
  useEffect(() => {
    const ask = () => signInAndReturn();
    window.addEventListener(MP_SIGN_IN_NEEDED, ask);
    return () => window.removeEventListener(MP_SIGN_IN_NEEDED, ask);
  }, []);

  /**
   * Something the household told us about their own records, kept.
   *
   * Optimistic and unawaited, both on purpose: the page holds the fact
   * immediately so the next person's question is already answered, and the
   * registration never waits on a write that is not part of it. If MP refuses
   * it, the worst case is the question being asked again — which is exactly
   * what happens today.
   */
  const learnRelationship = (fact: LearnedRelationship) => {
    setMe((current) =>
      current
        ? {
            ...current,
            people: current.people.map((p) => {
              if (p.contactId === fact.contactId && fact.relationshipId)
                return {
                  ...p,
                  relations: { ...p.relations, [fact.relatedContactId]: fact.relationshipId },
                };
              if (p.contactId === fact.relatedContactId && fact.gender)
                return { ...p, gender: fact.gender };
              return p;
            }),
          }
        : current
    );
    if (!eventId) return;
    void saveRelationship(apiBaseUrl, eventId, fact).catch(() => {
      /* the record keeps what it had; the form answer still stands */
    });
  };

  /**
   * A gender a question had to ask for. On screen at once, so every other
   * question about them already knows it, and to MP in the background; the
   * server only fills a gap.
   */
  const learnGender = (contactId: number, gender: 'male' | 'female') => {
    setMe((current) =>
      current
        ? {
            ...current,
            people: current.people.map((p) =>
              p.contactId === contactId && !p.gender ? { ...p, gender } : p
            ),
          }
        : current
    );
    if (!eventId) return;
    void saveGender(apiBaseUrl, eventId, contactId, gender).catch(() => {
      /* the draft still carries it, and the register route fills the gap */
    });
  };

  /**
   * The card a finished registration files itself into, and what to say on
   * the way there. Null except for the few seconds after somebody registers.
   */
  const whoCard = useRef<HTMLDivElement>(null);
  const [justRegistered, setJustRegistered] = useState<string[] | null>(null);

  const onRegistered = (guid: string, claimedSuccess = false, names?: string[]) => {
    setSheetOpen(false);
    setChosen(new Set());
    setGuests([]);
    setInvoiceGuid(guid);
    if (claimedSuccess) setClaimed(true);
    // Only when this is the end of a registration. Returning to a finished
    // one, or opening the page on an invoice link, is not an achievement and
    // gets no curtain.
    if (names?.length) setJustRegistered(names);
    // The URL is deliberately left alone. `setInvoiceGuid` and `refreshMe`
    // already know what was just bought, and the household read returns every
    // invoice on this event anyway — so writing the guid into the address bar
    // bought nothing and pinned the page to one invoice for good. Register a
    // second person later and that pin names the older one; share or bookmark
    // the page and it goes with you. Links that should carry an invoice are
    // built on purpose by `invoiceLink` and the share button.
    refreshMe();
  };

  const invoiceLink = useCallback(
    (guid: string) => {
      const u = new URL(pageUrl || window.location.href);
      u.search = '';
      if (eventId) u.searchParams.set('id', String(eventId));
      u.searchParams.set('invoice', guid);
      return u.toString();
    },
    [pageUrl, eventId]
  );

  /**
   * Invoices as they come back, kept because the page has to know which ones
   * it is showing: a registration already spelled out at the top of the card
   * has no business repeating itself three rows down.
   */
  const [invoiceViews, setInvoiceViews] = useState<Record<string, InvoiceResponse>>({});
  const onInvoiceLoaded = useCallback(
    (r: InvoiceResponse) => {
      if (!eventId && r.event) setEventId(r.event.id);
      const view = r.view;
      if (view.kind !== 'missing')
        setInvoiceViews((was) => ({ ...was, [view.state.invoiceGuid]: r }));
    },
    [eventId]
  );

  // Invoices to show on this page: the one in the URL, then the household's on this event.
  /**
   * The event's own colour, if it has one, put where every surface can read
   * it. Two roots, because the sheets and the pay dock do not live inside the
   * page's element — they portal to a host at the end of <body> so a fixed
   * overlay is not trapped in the website's stacking context. A token set on
   * one of them would recolour half the widget.
   *
   * Written as properties rather than a stylesheet: the value comes from a
   * database column, and a database column is never allowed to become CSS
   * text. `isHexColor` is the whole gate.
   */
  const overlayHost = usePortalContainer();
  /**
   * The event's own colour first, its gathering's second.
   *
   * A gathering is a heavy thing to invent just to make the buttons match the
   * poster, so `Events.Accent_Color` lets any event say it in MP. Where both
   * exist the event wins: it is the more specific of the two, and it is the
   * one a staff member set while looking at this event.
   */
  /**
   * AN EXPERIENCE (src/experiences, docs/event-experiences.md) owns the whole
   * page, its colours and the header's: when one applies, this component only
   * loads data and hands it over. The time machine can pick one for staff.
   */
  const [preview, setPreview] = usePreview(!!me?.viewer.isStaff);
  const experienceKey = experienceKeyFor(details, preview);
  // An experience paints the page itself; it may still name the header's
  // colours and the accent the shared registration sheet wears.
  const [sheetDress, setSheetDress] = useState<SheetDress | null>(null);
  // Before the event loads, the experience it will probably wear (the
  // registry's own matches, or this device's last visit): its skeleton and
  // its header colours show from the first frame instead of the stock page's.
  const hintedKey = details ? null : experienceHint(eventId, eventSlug ?? null);
  const chromeKey = experienceKey ?? hintedKey;
  useEffect(() => {
    if (details?.event.id) rememberExperience(details.event.id, eventSlug ?? null, experienceKey);
  }, [details?.event.id, eventSlug, experienceKey]);
  const xpChrome = chromeKey ? (EXPERIENCES[chromeKey]?.chrome?.header ?? null) : null;
  const accent = chromeKey
    ? (xpChrome?.accent ?? null)
    : (details?.event.accentColor ?? details?.gathering?.accentColor ?? null);
  const accentInk = chromeKey
    ? (xpChrome?.ink ?? null)
    : (details?.event.accentInk ?? details?.gathering?.accentInk ?? null);
  useEffect(() => {
    /**
     * Three places, because a sheet is not inside the page. `ResponsiveSheet`
     * portals to the widget's overlay host where the shell provides one (the
     * website, where a fixed element rendered in place is trapped under the
     * theme's header) and to <body> where it does not (the dev app). A token
     * set only on the article themed the page and left every sheet, dock and
     * menu wearing the platform's teal.
     */
    const roots = [rootRef.current, overlayHost ?? document.body].filter(Boolean) as HTMLElement[];
    const vars = isHexColor(accent) ? accentTokens(accent, accentInk) : null;
    const ink = isHexColor(accentInk) ? accentInk : null;
    for (const el of roots) {
      if (!vars) {
        el.style.removeProperty('--color-teal');
        el.style.removeProperty('--color-teal-deep');
        el.style.removeProperty('--color-teal-soft');
        el.style.removeProperty('--brand-secondary');
        el.style.removeProperty('--color-on-accent');
      } else {
        el.style.setProperty('--color-teal', vars.base);
        el.style.setProperty('--color-teal-deep', vars.deep);
        el.style.setProperty('--color-teal-soft', vars.soft);
        // The focus ring and the shadcn `accent` both come off this one, and
        // a teal ring round a selected row was the last thing on the page
        // still arguing with the artwork.
        el.style.setProperty('--brand-secondary', vars.base);
        // What can be read ON the colour, which is not always what the event
        // picked: cream over artwork is beautiful and cream on that same
        // orange is 2.69:1.
        el.style.setProperty('--color-on-accent', vars.onAccent);
      }
      // Over the artwork, where the event's own ink always wins.
      if (ink) el.style.setProperty('--color-on-art', ink);
      else el.style.removeProperty('--color-on-art');
    }
  }, [accent, accentInk, overlayHost, status]);

  /**
   * AND THE SITE'S OWN HEADER, FOR AS LONG AS IT IS STANDING ON THE ARTWORK.
   *
   * The transparent header puts the church's logo and its Give button on the
   * event's poster, where Woodside's teal is the same argument the page itself
   * stopped having when events got their own accent. See lib/chrome.ts: every
   * rule is scoped to the class Salient itself adds and removes, so scrolled
   * past the hero the header is the church's again with nothing to undo.
   */
  useEffect(() => {
    const vars = isHexColor(accent)
      ? chromeVarsFor(
          accent,
          accentInk,
          xpChrome?.onAccent ?? accentTokens(accent, accentInk).onAccent
        )
      : null;
    return paintChrome(document, vars);
  }, [accent, accentInk, xpChrome?.onAccent]);

  /**
   * THE BLACK BAR DOWN THE RIGHT EDGE OF A WHITE SITE.
   *
   * The theme never declares a `color-scheme`, so a laptop set to dark gets a
   * dark scrollbar on a page that has no dark mode. This says the page is
   * light, and tints the bar where the event has a colour. See lib/scrollbar.
   * Separate from the header above because it runs for every event: an event
   * with no colour still should not have a black bar.
   */
  useEffect(() => paintScrollbar(document, accent), [accent]);

  /**
   * THE HERO REACHES PAST THE COLUMN IT IS IN, AND UP TO THE TOP OF THE PAGE.
   *
   * Measured rather than assumed: the widget is dropped into whatever
   * container the WordPress template gives it — eleven wrappers deep, on
   * woodsidebible.org — and is never told how wide any of them are or what
   * padding they add. Three numbers go on the root as properties, the hero
   * pulls itself out and up by them and pads its content back in by them, so
   * the band runs to the edges of the screen and starts under the header
   * while every word in it stays on the column the cards below are on. See
   * `lib/bleed.ts` for why not `100vw`.
   *
   * Laid out before paint, so the hero is never briefly the width of the
   * column; re-measured when the widget changes width, which covers a turned
   * phone, a dragged window and the page reflowing around it.
   */
  useIsomorphicLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let lastWidths = '';
    const measure = () => {
      const page = document.documentElement.clientWidth;
      const mine = Math.round(root.getBoundingClientRect().width);
      // WIDTH ONLY. The observer watches the document, whose size also
      // changes every time the page gets taller — including because of the
      // pull-up this very function applies. Measuring on those is how a
      // resize loop starts.
      const widths = `${page}:${mine}`;
      if (widths === lastWidths) return;
      lastWidths = widths;

      // An ancestor that clips would cut the band back to the column and
      // leave the content padded in from it, which reads as broken rather
      // than wide. Then the page keeps the shape it already had.
      if (anyClips(overflowChain(root))) {
        for (const name of ['--ed-bleed-l', '--ed-bleed-r', '--ed-bleed-t', '--ed-headroom'])
          root.style.removeProperty(name);
        return;
      }

      // Measured with our own adjustments taken back off. The hero's negative
      // top margin moves the widget itself, so measuring the page as it
      // currently stands would measure the result of the last measurement,
      // and the two would argue with each other every frame.
      root.style.setProperty('--ed-bleed-t', '0px');
      root.style.setProperty('--ed-headroom', '0px');
      const rect = root.getBoundingClientRect();
      const body = document.body.getBoundingClientRect();
      const { left, right } = bleedFor(rect, {
        left: Math.max(0, body.left),
        right: Math.min(page, body.right),
      });
      const rise = riseFor(topBoxes(root), rect.top);
      // Where the hero is about to start, and how much of that a header
      // fixed to the top of the window is standing on. A site that makes
      // room for its own header measures nothing here; one whose header
      // floats over the page measures its height, and the hero keeps its
      // writing clear of it while the artwork still runs underneath.
      const heroTop = rect.top - rise;
      const headroom = headroomFor(topFixedBottom(document, heroTop), heroTop);
      root.style.setProperty('--ed-bleed-l', `${left}px`);
      root.style.setProperty('--ed-bleed-r', `${right}px`);
      root.style.setProperty('--ed-bleed-t', `${rise}px`);
      root.style.setProperty('--ed-headroom', `${headroom}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    observer.observe(document.documentElement);

    /**
     * A TAB COMING BACK FROM THE BACKGROUND HAS TO BE MEASURED AGAIN.
     *
     * Close Chrome on a phone, reopen it, and the hero had no headroom: the
     * site's header sat straight on top of the event title. A refresh fixed
     * it, which is the tell that the DOM was fine and only the measurement
     * was wrong.
     *
     * Two things conspire. Restoring a tab resizes it, which wakes the
     * observer, and at that instant the measurement cannot be trusted:
     * `elementsFromPoint` finds nothing while the page is still being put
     * back, and `innerHeight` reports the window mid animation, so a 60px
     * header can fail the "chrome, not a curtain" test against a height that
     * is briefly a fraction of the real one. Either way it reads no header
     * and writes a headroom of zero. Then `lastWidths` makes it permanent:
     * nothing has changed width since, so every later pass returns early and
     * the zero stands until the page is reloaded.
     *
     * So: forget the last widths and measure again once the browser has
     * settled. Twice, because one frame is enough for the hit testing to
     * work and not always enough for the toolbars to finish moving.
     */
    let pending: number[] = [];
    const remeasure = () => {
      for (const id of pending) window.clearTimeout(id);
      pending = [0, 250].map((wait) =>
        window.setTimeout(() => {
          lastWidths = '';
          measure();
        }, wait)
      );
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) remeasure();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') remeasure();
    };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      observer.disconnect();
      for (const id of pending) window.clearTimeout(id);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [status]);

  const invoiceGuids = useMemo(() => {
    const out: string[] = [];
    if (invoiceGuid) out.push(invoiceGuid);
    for (const inv of me?.invoices ?? []) if (!out.includes(inv.guid)) out.push(inv.guid);
    return out;
  }, [invoiceGuid, me]);

  if (status === 'missing' || status === 'error') {
    return (
      <div className="ed">
        <div className="card flex flex-col items-center gap-4 px-6 py-14 text-center">
          <p className="text-navy text-lg font-bold">
            {status === 'missing' ? 'We could not find that event.' : 'Something went wrong.'}
          </p>
          <p className="text-slate max-w-md text-sm">
            {status === 'missing'
              ? 'It may have been removed, or the link may be incomplete.'
              : 'Please try again in a moment.'}
          </p>
          <a href={finderUrl} className="btn btn-navy">
            <ChevronLeft className="h-4 w-4" /> All events
          </a>
        </div>
      </div>
    );
  }

  // Invoice alone, no event behind it: the card is the page.
  if (!eventId && !eventSlug && invoiceGuid && status === 'ready') {
    return (
      <div className="ed mx-auto max-w-xl">
        <InvoiceCard
          apiBaseUrl={apiBaseUrl}
          guid={invoiceGuid}
          claimedSuccess={claimed}
          pageUrl={pageUrl || bareUrl()}
          detailsUrl={detailsUrl}
          onLoaded={onInvoiceLoaded}
        />
      </div>
    );
  }

  /**
   * The same root, and the same dock around it, so React keeps the article
   * rather than throwing it away and building another one. That matters for
   * more than tidiness: the bleed measurement lives on this element, and a
   * remount would drop the custom properties and let the hero be column
   * width for a frame before it corrected itself.
   */
  if (!details) {
    const XpSkeleton = hintedKey ? EXPERIENCES[hintedKey]?.Skeleton : undefined;
    return (
      <PayDockHost>
        <article className={XpSkeleton ? 'ed' : 'ed pb-12'} ref={rootRef}>
          {XpSkeleton ? <XpSkeleton /> : <Skeleton finderUrl={finderUrl} />}
        </article>
      </PayDockHost>
    );
  }

  const { event, state, product, form, siblings, spotsLeft, links } = details;
  const people = me?.people ?? [];

  const going = people.filter((p) => p.already === 'registered');
  const holding = people.filter((p) => p.already === 'pending');
  const everyoneIn = people.length > 0 && going.length + holding.length === people.length;
  const oneDay = !event.end || sameDay(event.start, event.end);
  const addressLine = [event.location.line1, event.location.city, event.location.postal]
    .filter(Boolean)
    .join(', ');
  /**
   * What the night is, in the hero, where the title used to be — the poster
   * beside it is already saying the name. Only where there is a poster: an
   * event with no art has nothing else saying what it is called, so that one
   * keeps its title and its About section where they were.
   */
  const blurb = event.image ? event.description || event.shortDescription || null : null;
  // Church Wide is Congregation_ID 1: an event every campus is invited to,
  // which is not the same fact as where it happens.
  const campusLabel = event.campus.id === 1 ? 'All Campuses' : event.campus.name;
  const price = event.basePrice;
  // Several registrations means no single price: the one somebody picks
  // decides it, so the panel names the range and the cards name the number.
  const doors = details.gathering?.doors ?? [];
  const finishSheet = invoiceGuid ? (
    <FinishSheet
      apiBaseUrl={apiBaseUrl}
      invoiceGuid={invoiceGuid}
      open={finishOpen}
      onClose={() => setFinishOpen(false)}
      event={details.event}
      form={details.form}
      doorForms={details.doorForms ?? {}}
      doors={doors}
      campuses={details.campuses ?? []}
    />
  ) : null;
  const manyWaysIn = doors.length > 1;
  /**
   * WHO IS COMING IS THE FIRST QUESTION OF EVERY REGISTRATION, NOT JUST THE
   * COMPLICATED ONES.
   *
   * The panel used to show the household only where an event had several ways
   * in, and a stock MP event — one registration, one price — got a Register
   * button that opened a sheet to ask the same thing. The household is the
   * same household either way, the sheet already skips its own "who" step
   * when the page has answered it, and a signed-in person should not have to
   * open anything to see that their wife and three kids are here to be
   * ticked.
   *
   * It needs somebody to list, so a signed-out visitor and anyone MP has no
   * relationships for still get the plain button: a list of nobody is worse
   * than the question.
   */
  // `me`, not `viewer`: the viewer object is shaped further down, and this is
  // the same fact — somebody is signed in and MP knows their household.
  const picksPeople = manyWaysIn || (Boolean(me) && people.length > 0);
  // People whose registration is already spelled out by a card above the
  // list. Money owed docks to the corner instead, so those rows stay: the
  // dock can be put away, and somebody has to still see they have a seat.
  const saidAbove = new Set<number>();
  for (const res of Object.values(invoiceViews)) {
    if (res.view.kind !== 'paid' && res.view.kind !== 'confirming') continue;
    for (const p of res.view.state.participants) if (p.contactId) saidAbove.add(p.contactId);
  }

  // Any registration on its own clock: see the note by the closing date.
  const ownWindows = doors.some((d) => d.opensAt || d.closesAt);
  const doorPrices = [...doors.map((d) => d.price)].sort((a, b) => a - b);
  // What each person is already registered as, read off their invoice line.
  const registeredAs = new Map<number, string>();
  // And which registrations the household actually holds, which is what
  // decides whether the agenda's volunteer and leader lines are theirs.
  const slugOf = new Map(doors.map((d) => [d.label, d.slug ?? String(d.id)]));
  const mineSlugs = new Set<string>();
  for (const invoice of me?.invoices ?? [])
    for (const line of invoice.people) {
      const firstOption = line.options[0];
      if (firstOption) registeredAs.set(line.contactId, firstOption);
      for (const option of line.options) {
        const slug = slugOf.get(option);
        if (slug) mineSlugs.add(slug);
      }
    }
  const viewer = me
    ? {
        firstName: me.viewer.firstName,
        lastName: me.viewer.lastName,
        email: me.viewer.email,
        phone: me.viewer.phone,
      }
    : null;
  // Serving is the registration where there is no other (see the API's
  // serving-door.ts), and "Register, it is free" is the wrong sentence for it.
  const servingOnly = Boolean(details.gathering?.synthetic);
  const onlyDoor = doors.length === 1 ? (doors[0] ?? null) : null;
  // The one way in, in the button: "Sign up to serve", "Register as Student".
  const doorWord = onlyDoor
    ? servingOnly || onlyDoor.takesShifts
      ? 'Sign up to serve'
      : `Register as ${onlyDoor.label}`
    : null;
  const label = doorWord
    ? everyoneIn
      ? 'Register a guest'
      : doorWord
    : everyoneIn
      ? 'Register a guest'
      : price
        ? 'Register'
        : 'Register, it is free';
  /**
   * The bar says the price on its own line, so its button must not say it
   * again. "Free / Register, it is free" was the same word twice, and the
   * half that came from `Base_Price` was not even true: Flannel Fest has no
   * base price and charges a Student $10.
   */
  const dockLabel = everyoneIn ? 'Register a guest' : (doorWord ?? 'Register');
  const dockPrice = askingPriceFor(doors, price, fmtMoney);
  const dockWays = waysInSummary(doors);

  /**
   * AN EXPERIENCE takes the page body. The invoices, payment and data loading
   * above stay the same: it receives the proven invoice cards as a slot and
   * opens the shared registration sheet rather than growing its own.
   */
  const experienceRegistration = state.kind === 'open' && (
    <RegistrationSheet
      key={sheetSeeded ? 'from-panel' : 'from-dock'}
      apiBaseUrl={apiBaseUrl}
      event={event}
      seriesDates={event.registerIntoSeries ? siblings.length + 1 : 0}
      product={product}
      form={form}
      viewer={viewer}
      people={people}
      open={sheetOpen}
      onClose={() => setSheetOpen(false)}
      pageUrl={pageUrl || bareUrl()}
      onRegistered={onRegistered}
      signIn={signInAndReturn}
      invoiceLink={invoiceLink}
      doors={doors}
      shifts={details.shifts ?? []}
      doorForms={details.doorForms ?? {}}
      campuses={details.campuses ?? []}
      medicalTypes={me?.medicalTypes ?? []}
      householdAddress={me?.address ?? null}
      preselect={[]}
      preselectGuests={[]}
      onLearn={learnRelationship}
      onLearnGender={learnGender}
      dress={sheetDress ?? undefined}
      onOpenRegistered={
        experienceKey
          ? (contactId) => {
              // The experience shows her place (its place sheet); this only
              // says whose, the same event the floating ticket listens for.
              setSheetOpen(false);
              window.dispatchEvent(
                new CustomEvent('event-details:open-place', { detail: { contactId } })
              );
            }
          : undefined
      }
    />
  );

  if (experienceKey && details.page) {
    const invoiceCards = invoiceGuids.length ? (
      <div className="ed flex flex-col gap-3">
        {invoiceGuids.map((guid, i) => (
          <InvoiceCard
            key={guid}
            quiet={invoiceGuids
              .slice(0, i)
              .some((before) => invoiceViews[before]?.view.kind === invoiceViews[guid]?.view.kind)}
            apiBaseUrl={apiBaseUrl}
            guid={guid}
            claimedSuccess={claimed && guid === invoiceGuid}
            pageUrl={pageUrl || bareUrl()}
            detailsUrl={detailsUrl}
            embedded
            compact
            detail
            dockWhenOwed
            photoOf={(contactId) => people.find((p) => p.contactId === contactId)?.image ?? null}
            onLoaded={onInvoiceLoaded}
          />
        ))}
      </div>
    ) : null;
    return (
      <PayDockHost>
        <section className="ed" ref={rootRef}>
          <ExperienceHost
            experienceKey={experienceKey}
            preview={preview}
            setPreview={setPreview}
            details={details}
            apiBaseUrl={apiBaseUrl}
            me={me}
            onSignIn={signInAndReturn}
            onRegister={() => openSheet(false)}
            setRegistrationDress={setSheetDress}
            invoiceSlot={invoiceCards}
            shareUrl={pageUrl || bareUrl()}
          />
          {experienceRegistration}
          {finishSheet}
        </section>
      </PayDockHost>
    );
  }

  return (
    <PayDockHost>
      <article className="ed pb-12" ref={rootRef}>
        {/* Hero. The art is 16:9 and shown whole, and the band behind it is the
          same art blown up and blurred: a frosted wash of the promo, the way
          the platform's event hero does it. The navy is only what shows when
          an event has no art at all. */}
        {/* IT RUNS TO THE EDGES OF THE SCREEN. A card of artwork floating in a
          column read as an illustration of the event; a band that touches
          both sides reads as the event itself, which is what the top of the
          page is for. `bleed` reaches out by the measured gap to the window
          and `bleed-inner` pads the same gap back, so the band is the width
          of the screen and every word in it is on the column the cards below
          are on. No corner radius: a rounded full-width band is a card that
          has outgrown the page. */}
        {/* The band clips itself, not the hero. `overflow-hidden` on the
          section was what kept the blown-up art inside the rounded corners,
          and it also cut the directions menu off at the hero's bottom edge —
          so the wash gets its own clipped layer and the section stops
          clipping anything. */}
        {/* One colour for everything over the art, inherited: the event's ink
          when it has one, white when it does not. The half-tones are opacity
          rather than another colour, so they follow it. */}
        <section className="bg-navy on-art bleed relative">
          {event.image && (
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <img
                src={event.image}
                alt=""
                className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl"
              />
              {/* One even scrim, enough for white type on any artwork. */}
              <div className="absolute inset-0 bg-black/35" />
            </div>
          )}
          <div className="bleed-inner bleed-headroom relative py-6 sm:py-8">
            {/* The poster leads, under the way out, and what the night is
              sits beside it. The art is the first thing anybody recognises —
              the students have seen it on a screen at church all month — so
              it goes where reading starts rather than off to the right. */}
            {/* The way out, and the two things anybody does with a page they
              are not registering on right now: put it in a calendar, send it
              to somebody. Icons, because both are a shape people know, and on
              the hero's top line rather than in the facts card, where they
              were sitting on the row that answers "when". */}
            <div className="mb-6 flex items-center gap-2">
              <a
                href={finderUrl}
                className="kicker inline-flex w-fit items-center gap-1 transition-[filter] hover:brightness-110"
              >
                <ChevronLeft className="h-3 w-3" /> All events
              </a>
              <div className="ml-auto flex items-center gap-1.5">
                <AddToCalendar icsHref={links.ics} googleHref={links.googleCalendar} />
                <ShareButton title={event.title} url={pageUrl || `${detailsUrl}${event.id}`} />
              </div>
            </div>
            <div className={`grid gap-6 sm:gap-8 ${event.image ? 'lg:grid-cols-[26rem_1fr]' : ''}`}>
              <div className="flex flex-col gap-5">
                {event.image && (
                  <img
                    src={event.image}
                    alt={event.title}
                    className="shadow-lift aspect-video w-full rounded-2xl object-cover ring-1 ring-white/15"
                  />
                )}
              </div>
              {/* THE ART SAYS THE NAME. Woodside's promo pieces are built
                around the title — a poster that does not say what it is for
                is not a poster — so setting it again beside the image was the
                same three words twice, in the one place there is room to say
                something else. What the night actually is goes here instead,
                at the size the title used to be, and the heading stays for
                the people reading this with their ears. An event with no art
                keeps its title, since then nothing else is saying it. */}
              <div className="min-w-0 self-center">
                <h1
                  className={
                    blurb
                      ? 'sr-only'
                      : 'text-3xl leading-tight font-extrabold tracking-normal normal-case sm:text-5xl'
                  }
                >
                  {event.title}
                </h1>
                {blurb && (
                  <Fact tone="hero" icon={<Info className="h-5 w-5 opacity-70" />} label="What">
                    <div
                      className="mp-html mp-html-hero text-lg leading-relaxed opacity-90 sm:text-xl lg:text-2xl"
                      dangerouslySetInnerHTML={{ __html: blurb }}
                    />
                  </Fact>
                )}
                {event.cancelled && (
                  <p className="bg-rose mt-3 inline-block rounded-full px-3 py-1 text-sm font-bold">
                    Cancelled
                  </p>
                )}
                {/* WHAT, WHEN, WHERE, IN THAT ORDER AND IN ONE PLACE. The two
                  facts every poster ends with were in a card below the fold on
                  a phone, under the artwork somebody had already scrolled
                  past. They belong to the same glance as the sentence that
                  says what the night is, so they sit under it — the card below
                  keeps the facts the poster does not carry. */}
                <div className="mt-2">
                  <Fact tone="hero" icon={<Clock className="h-5 w-5 opacity-70" />} label="When">
                    <div className="font-bold">{fmtDay(event.start, true)}</div>
                    <div className="text-sm opacity-70">
                      {fmtTime(event.start)}
                      {event.end
                        ? ` to ${oneDay ? '' : `${fmtDay(event.end)} `}${fmtTime(event.end)}`
                        : ''}
                    </div>
                  </Fact>
                  <WhereFact
                    tone="hero"
                    name={event.location.name}
                    address={addressLine}
                    lat={event.location.lat}
                    lng={event.location.lng}
                    accent={details.gathering?.accentColor ?? undefined}
                    online={
                      event.onlineMeetingLink ? (
                        <a
                          href={event.onlineMeetingLink}
                          className="inline-flex items-center gap-1 text-sm font-semibold"
                          target="_blank"
                          rel="noopener"
                        >
                          Join online <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : null
                    }
                  >
                    <span className="block font-bold">
                      {event.location.name ?? event.campus.name ?? 'Location to be announced'}
                    </span>
                    {addressLine && <span className="block text-sm opacity-70">{addressLine}</span>}
                  </WhereFact>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="grid gap-8 pt-8 lg:grid-cols-[1fr_22rem]">
          {/* Body */}
          <div className="min-w-0 space-y-8">
            {/* What the hero does not say: who is invited, and whose night
              it is. "Troy Campus" and "All Campuses" are both true here, and
              only the labels say which is the address and which is the
              invitation — which is why the campus row stays labelled even
              with the address up in the hero. */}
            {(campusLabel || event.ministryName || event.primaryContactName) && (
              <section className="card divide-mist divide-y">
                {/* Built like the contact row below it: a mark in the icon
                  column for what the row IS, and the campus's own art beside
                  the answer, where the face sits two rows down. */}
                {campusLabel && (
                  <Fact icon={<Church className="text-teal h-5 w-5" />} label="Campus">
                    <span className="flex items-center gap-3">
                      <CampusMark campus={event.campus} className="h-9 w-9 shrink-0" />
                      <span className="text-navy font-bold">{campusLabel}</span>
                    </span>
                  </Fact>
                )}
                {event.ministryName && (
                  <Fact icon={<Users className="text-teal h-5 w-5" />} label="Ministry">
                    {/* A ministry with a wordmark of its own says it in its own
                      hand. The name is still what a screen reader reads, and
                      still what shows if the file is missing or fails to load:
                      the logo is how this ministry is recognised, the name is
                      what it is called, and the row means the same either way. */}
                    {event.ministryLogo ? (
                      <img
                        src={event.ministryLogo}
                        alt={event.ministryName}
                        className="h-6 w-auto max-w-[11rem] object-contain object-left"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`text-navy font-bold ${event.ministryLogo ? 'hidden' : ''}`}>
                      {event.ministryName}
                    </div>
                  </Fact>
                )}
                {/* Who answers for this event is a fact about it, and it was
                  sitting alone at the foot of the page under everything a
                  person might have had a question about. Last row: you read
                  the night, then you find the person. */}
                {event.primaryContactName && (
                  <ContactCard
                    variant="row"
                    apiBaseUrl={apiBaseUrl}
                    eventId={event.id}
                    name={event.primaryContactName}
                    image={event.primaryContactImage}
                    people={people}
                    viewerContactId={me?.viewer?.contactId ?? null}
                    event={{
                      image: event.image,
                      title: event.title,
                      start: event.start,
                      end: event.end,
                    }}
                  />
                )}
              </section>
            )}

            {!blurb && (event.description || event.shortDescription) && (
              <section>
                <h2 className="kicker mb-3">About</h2>
                <div
                  className="mp-html"
                  dangerouslySetInnerHTML={{
                    __html: event.description || event.shortDescription || '',
                  }}
                />
                {event.readMoreUrl && (
                  <a
                    href={event.readMoreUrl}
                    target="_blank"
                    rel="noopener"
                    className="btn btn-quiet mt-2"
                  >
                    Read more <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </section>
            )}

            {event.meetingInstructions && (
              <section>
                <h2 className="kicker mb-3">When you arrive</h2>
                <div
                  className="mp-html bg-teal-soft rounded-2xl px-5 py-4"
                  dangerouslySetInnerHTML={{ __html: event.meetingInstructions }}
                />
              </section>
            )}

            {details?.gathering && (
              <Agenda
                blocks={details.gathering.agenda}
                doors={details.gathering.doors}
                mine={mineSlugs}
              />
            )}

            <OtherDates siblings={siblings} detailsUrl={detailsUrl} title={event.title} />
          </div>

          {/* Register */}
          <aside ref={panelRef} className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            {/* ONE CARD. The money and the people are one thing to whoever is
              looking: a payment card above a list saying the same person is
              going was two answers to "where do I stand". The invoice leads,
              because an unpaid seat is the only thing on this page with a
              clock on it, and the list below says who each person is and what
              they are registered as. */}
            <div ref={whoCard} className="card space-y-4 p-5">
              {/* The card's own heading, and it stays at the top of it: what
                  follows is who is going and who could be, settled seats
                  first. */}
              {state.kind === 'open' &&
                Boolean(viewer) &&
                picksPeople &&
                (onlyDoor ? (
                  // One way in names the card: "Serve", not "Who" over "Serve".
                  <div className="flex items-baseline justify-between">
                    <p className="kicker">{onlyDoor.label}</p>
                    {onlyDoor.price > 0 && (
                      <span className="text-navy text-lg font-extrabold">
                        {fmtMoney(onlyDoor.price)}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="kicker">Who</p>
                ))}
              {/* Money still owed leaves the card and docks to the corner; a
                registration that is settled, confirming or cancelled stays
                here, because it is news rather than a thing to do. */}
              {/* The first card of a kind carries the heading; the rest are
                quiet. A mixed party writes two invoices — free seats settled,
                the rest to pay — and "You are in." twice reads as a bug. */}
              {invoiceGuids.map((guid, i) => (
                <InvoiceCard
                  key={guid}
                  quiet={invoiceGuids
                    .slice(0, i)
                    .some(
                      (before) => invoiceViews[before]?.view.kind === invoiceViews[guid]?.view.kind
                    )}
                  apiBaseUrl={apiBaseUrl}
                  guid={guid}
                  claimedSuccess={claimed && guid === invoiceGuid}
                  pageUrl={pageUrl || bareUrl()}
                  detailsUrl={detailsUrl}
                  embedded
                  compact
                  bare
                  detail
                  dockWhenOwed
                  photoOf={(contactId) =>
                    people.find((p) => p.contactId === contactId)?.image ?? null
                  }
                  onLoaded={onInvoiceLoaded}
                />
              ))}

              {state.kind === 'open' && (
                <>
                  {/* No summary price: what somebody pays depends on which
                    registration is theirs, so the number lives on the
                    registration itself. And no "Registration" label: Who is
                    the heading this card needed. */}
                  {/* ONE WAY IN IS NAMED ONCE, HERE. With several, each row
                    carries pills saying which are that person's; with one the
                    pills are gone, so the card says what this is — Serve —
                    and its price only if it has one. No ways in at all is a
                    plain registration, named by its price as before. */}
                  {onlyDoor
                    ? // Signed in, the card's heading already says it.
                      !(viewer && picksPeople) && (
                        <div className="flex items-baseline justify-between">
                          <span className="kicker">{onlyDoor.label}</span>
                          {onlyDoor.price > 0 && (
                            <span className="text-navy text-lg font-extrabold">
                              {fmtMoney(onlyDoor.price)}
                            </span>
                          )}
                        </div>
                      )
                    : !manyWaysIn &&
                      price != null &&
                      price > 0 && (
                        <div className="flex items-baseline justify-between">
                          <span className="kicker">Registration</span>
                          <span className="text-navy text-lg font-extrabold">
                            {fmtMoney(price)}
                          </span>
                        </div>
                      )}
                  {/* No event-wide "spots left": capacity is per registration
                    and per shift, so one number for the whole night answers a
                    question nobody asked. Fullness is said where it bites, on
                    the registration and on the shift. */}
                  {spotsLeft === 0 && (
                    <p className="text-rose text-sm font-semibold">
                      <Users className="mr-1 inline h-4 w-4" />
                      Full
                    </p>
                  )}
                  {/* One closing date, only while it is the whole truth. As soon
                    as a registration carries its own window the pills say it
                    per person and per type, and a single line here would be
                    wrong for whichever one closed early. */}
                  {event.registrationEnd && !ownWindows && (
                    <p className="text-slate text-xs">Closes {fmtDay(event.registrationEnd)}</p>
                  )}
                  {spotsLeft === 0 ? (
                    <button type="button" className="btn btn-quiet w-full" disabled>
                      Registration full
                    </button>
                  ) : (
                    <>
                      {/* Ticking people IS starting the registration: the sheet
                        opens on the question the page has not asked. */}
                      {picksPeople ? (
                        <SignUp
                          doors={doors}
                          people={people}
                          registeredAs={registeredAs}
                          selected={chosen}
                          onToggle={toggleChosen}
                          guests={guests}
                          onAddGuest={addGuest}
                          onEditGuest={setAddingGuest}
                          onRemoveGuest={(k) => setGuests((was) => was.filter((g) => g.key !== k))}
                          onContinue={() => openSheet(true)}
                          signedIn={Boolean(viewer)}
                          signIn={signInAndReturn}
                          invoiceLink={invoiceLink}
                          onOpenInvoice={setOpenInvoice}
                          hide={saidAbove}
                        />
                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary w-full !py-4 !text-base"
                          onClick={() => openSheet(true)}
                        >
                          <Ticket className="h-5 w-5" />
                          {label}
                        </button>
                      )}
                      <RegistrationSheet
                        /* A different way in is a different beginning, so the
                           sheet starts over rather than resuming a flow that
                           began from the panel. */
                        key={sheetSeeded ? 'from-panel' : 'from-dock'}
                        apiBaseUrl={apiBaseUrl}
                        event={event}
                        seriesDates={event.registerIntoSeries ? siblings.length + 1 : 0}
                        product={product}
                        form={form}
                        viewer={viewer}
                        people={people}
                        open={sheetOpen}
                        onClose={() => setSheetOpen(false)}
                        pageUrl={pageUrl || bareUrl()}
                        onRegistered={onRegistered}
                        signIn={signInAndReturn}
                        invoiceLink={invoiceLink}
                        doors={doors}
                        shifts={details.shifts ?? []}
                        doorForms={details.doorForms ?? {}}
                        campuses={details.campuses ?? []}
                        medicalTypes={me?.medicalTypes ?? []}
                        householdAddress={me?.address ?? null}
                        preselect={sheetSeeded ? [...chosen] : []}
                        preselectGuests={sheetSeeded ? guests.filter(guestReady) : []}
                        onLearn={learnRelationship}
                        onLearnGender={learnGender}
                      />
                    </>
                  )}
                  {/* Under the button, because that is what it is about: the
                    question it answers only occurs to somebody who has just
                    decided to press it. */}
                  {event.registerIntoSeries && siblings.length > 0 && (
                    <SeriesNote dates={siblings.length + 1} />
                  )}
                </>
              )}
              {state.kind === 'elsewhere' && (
                <>
                  <span className="kicker">Registration</span>
                  <p className="text-slate-deep text-sm">
                    This event takes registrations on another site.
                  </p>
                  <a
                    href={state.url}
                    target="_blank"
                    rel="noopener"
                    className="btn btn-primary w-full !py-4"
                  >
                    Register <ExternalLink className="h-4 w-4" />
                  </a>
                </>
              )}
              {state.kind === 'not-yet' && (
                <>
                  <span className="kicker">Registration</span>
                  <p className="text-navy text-sm font-semibold">
                    Opens {state.opensAt ? fmtDay(state.opensAt) : 'soon'}
                  </p>
                </>
              )}
              {state.kind === 'closed' && (
                <>
                  <span className="kicker">Registration</span>
                  <p className="text-navy text-sm font-semibold">Registration has closed.</p>
                  {event.primaryContactName && (
                    <p className="text-slate text-xs">
                      Ask {event.primaryContactName} if there is still room.
                    </p>
                  )}
                </>
              )}
              {state.kind === 'cancelled' && (
                <p className="text-rose text-sm font-semibold">This event has been cancelled.</p>
              )}
              {state.kind === 'none' && (
                <>
                  <span className="kicker">Just come</span>
                  <p className="text-slate-deep text-sm">
                    No registration needed. Add it to your calendar and we will see you there.
                  </p>
                </>
              )}
              {state.kind !== 'open' && (
                <div className="flex flex-wrap gap-2">
                  <AddToCalendar icsHref={links.ics} googleHref={links.googleCalendar} />
                </div>
              )}
            </div>
          </aside>
        </div>
        {/* Adding somebody is its own short interview: name, then who they
            are to you, then whether they join the household. A sheet because
            those are pages, and because the questions still to come — a
            match to confirm, a gender a chip did not imply — are pages too. */}
        <AddPerson
          open={Boolean(addingGuest)}
          draft={guests.find((g) => g.key === addingGuest) ?? null}
          onChange={(patch) =>
            setGuests((was) => was.map((g) => (g.key === addingGuest ? { ...g, ...patch } : g)))
          }
          onSave={saveNewPerson}
          formerMembers={formerMembers ?? undefined}
          onBringBack={bringBackFormer}
          /**
           * Closing without finishing throws the draft away.
           *
           * A half-typed person used to stay on the list as "Someone else —
           * tap to finish adding them", which put a row in the household card
           * for somebody who does not exist anywhere yet. The card is a list of
           * people; a form in progress is not one of them.
           *
           * Anything finished — added, or waiting on a confirmation — has
           * `saved` set and survives.
           */
          onClose={() => {
            setGuests((was) =>
              was.filter((g) => g.key !== addingGuest || g.saved || guestReady(g))
            );
            setAddingGuest(null);
          }}
          onRemove={() => {
            setGuests((was) => was.filter((g) => g.key !== addingGuest));
            setAddingGuest(null);
          }}
        />

        {/* One person's row opens the whole invoice, because a household's
            registration is one receipt and the other names on it are the
            answer to "what did I pay for". */}
        <ResponsiveSheet
          open={Boolean(openInvoice)}
          onClose={() => setOpenInvoice(null)}
          maxWidth="max-w-lg"
          panelClassName="bg-ground text-ink"
          noPanelPadding
          hideCloseButton
          header={
            <div className="ed flex items-start gap-3 px-5 pt-5 pb-3">
              <div className="min-w-0 flex-1">
                <p className="kicker">Registration</p>
                <p className="text-navy truncate text-lg font-extrabold">{event.title}</p>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpenInvoice(null)}
                className="bg-mist hover:bg-line text-navy flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          }
        >
          <SheetPage name="main">
            <div className="ed px-5 pb-6">
              {openInvoice && (
                <InvoiceCard
                  apiBaseUrl={apiBaseUrl}
                  guid={openInvoice}
                  pageUrl={pageUrl || bareUrl()}
                  detailsUrl={detailsUrl}
                  embedded
                  bare
                  detail
                  photoOf={(contactId) =>
                    people.find((p) => p.contactId === contactId)?.image ?? null
                  }
                />
              )}
            </div>
          </SheetPage>
        </ResponsiveSheet>
      </article>
      {/*
       * Into the overlay host, not the article: a fixed element rendered in
       * place is trapped under the theme's header on the website, which is
       * the same reason the sheets portal there. The accent tokens are
       * already set on that host by the effect above, so the panel arrives
       * wearing the event's colour.
       */}
      {finishSheet}
      {justRegistered &&
        overlayHost &&
        createPortal(
          <RegisteredCurtain
            names={justRegistered}
            title={event?.title ?? 'This event'}
            start={event?.start ?? null}
            end={event?.end ?? null}
            image={event?.image ?? null}
            where={event?.location.name ?? null}
            target={() => whoCard.current}
            onDone={() => setJustRegistered(null)}
          />,
          overlayHost
        )}

      {/* The bar at the bottom of a phone. Only while there is something to
          register for: not when the event takes registrations elsewhere, not
          when it is full, and not while the sheet it opens is already open. */}
      {state.kind === 'open' && spotsLeft !== 0 && (
        <RegisterDock
          label={everyoneIn ? 'Register a guest' : 'Register'}
          ways={dockWays}
          anchor={panelRef}
          hidden={sheetOpen}
          onOpen={() => openSheet(false)}
        />
      )}
    </PayDockHost>
  );
}

/**
 * One labelled fact in the header card: icon, eyebrow, then the fact itself.
 * The eyebrow is what lets "Troy Campus" and "All Campuses" sit two rows
 * apart without reading as a contradiction.
 */
function Fact({
  icon,
  label,
  children,
  onClick,
  expanded,
  after,
  tone = 'card',
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  /** Makes the whole row the button, chevron and all. */
  onClick?: () => void;
  expanded?: boolean;
  /** Anything that cannot live inside a button — a link of its own. */
  after?: React.ReactNode;
  /** 'hero' is the same fact on the artwork: white type, tighter rows. */
  tone?: 'card' | 'hero';
}) {
  const hero = tone === 'hero';
  const body = (
    <>
      {/* Centred on the eyebrow, not the top of the block: the label is what
          the icon is labelling, and a mark floating above "WHAT" reads as
          belonging to neither line. h-4 is the eyebrow's own line box. */}
      <span className="flex h-4 w-5 shrink-0 items-center justify-center">{icon}</span>
      <span className="min-w-0 flex-1">
        {/* One label style, the site's, in both places. `.kicker` is an
            unlayered rule from the form kit, so its colour beats a Tailwind
            utility unless the utility says it means it.

            NO OPACITY ON THE ART. These sat at 60%, and over a dark photograph
            that is not a quieter beige — it is the beige mixed with whatever
            happens to be behind it, which is why the colour staff chose kept
            reading darker than the value they picked. The hierarchy is already
            in the type: Bebas caps at 0.95rem against the sentence underneath
            is a label either way. */}
        <span className={`kicker mb-1 block ${hero ? 'on-art-label' : ''}`}>{label}</span>
        {children}
      </span>
      {/* Centred on the row, not aligned to the first line: it belongs to the
          whole row, which is the button. */}
      {onClick && (
        <ChevronRight
          className={`h-4 w-4 shrink-0 self-center transition-transform ${hero ? 'opacity-60' : 'text-slate'} ${expanded ? 'rotate-90' : ''}`}
        />
      )}
    </>
  );
  if (!onClick)
    return (
      <div className={`flex items-start gap-3 ${hero ? 'py-2' : 'p-4'}`}>
        {body}
        {after}
      </div>
    );
  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        aria-haspopup="menu"
        aria-expanded={expanded}
        className={`flex w-full items-start gap-3 text-left transition ${
          hero ? '-mx-2 rounded-2xl px-2 py-2 hover:bg-white/10' : 'hover:bg-mist/60 p-4'
        }`}
      >
        {body}
      </button>
      {/* Outside the button: a link inside one is a link nobody can use. */}
      {after && <div className={hero ? 'pt-1 pl-8' : '-mt-2 pb-4 pl-12'}>{after}</div>}
    </div>
  );
}

/** A campus's own mark, or the Woodside one, which is also what every campus at once looks like. */
function CampusMark({
  campus,
  className,
}: {
  campus: { id: number; svgUrl: string | null };
  className?: string;
}) {
  // A campus's own art is their identity and is never recoloured. The
  // Woodside mark here is not the church's logo, it is the word "everybody" —
  // so it takes the page's colour like every other mark on it.
  if (campus.svgUrl)
    return <img src={campus.svgUrl} alt="" aria-hidden className={`${className} object-contain`} />;
  return <WoodsideLogo tone="current" className={`${className} text-teal`} />;
}

/**
 * THE SHAPE OF THE PAGE, BEFORE THE PAGE.
 *
 * The old skeleton was a rounded box at column width, and the hero it stood
 * in for is a band that runs to both edges of the screen with square
 * corners. So the load ended in a jump: the grey box snapped outwards and
 * everything under it moved. A placeholder whose only job is to stop the
 * page moving cannot be a different shape from the thing it is holding a
 * place for.
 *
 * It wears the same `bleed` classes as the real hero and sits inside the
 * same root, so it is measured the same way and stands in exactly the space
 * the artwork is about to occupy.
 *
 * WHAT IS NOT A SKELETON HERE. "All events" is a link to a URL the widget
 * was configured with — no database was ever going to be asked about it, so
 * it is drawn properly from the first frame rather than pulsing grey for a
 * second and then becoming itself. Only the things that genuinely have to be
 * read from MP get a loading state: the artwork, the words, the facts, and
 * who is going.
 *
 * THE BAND IS DARK, AND THAT IS NOT A GUESS AT THE EVENT'S COLOUR. It was
 * neutral grey, on the reasoning that guessing wrong about an event's colour
 * looks worse than not guessing. Then the site's header went transparent over
 * this band, which turns its logo and its menu white — and white on light grey
 * is nothing at all. So the placeholder is the church's navy, the same colour
 * the real hero falls back to when an event has no artwork: not a guess at
 * anything, and something the header can always be read against.
 */
/** A bar standing in for a value, on the artwork or on paper. */
function Bar({ w, art = false }: { w: string; art?: boolean }) {
  return <div className={`h-4 ${art ? 'skeleton-art' : 'skeleton'}`} style={{ width: w }} />;
}

function Skeleton({ finderUrl }: { finderUrl: string }) {
  return (
    <>
      <section className="bleed bg-navy on-art relative overflow-hidden">
        <div className="aurora" aria-hidden />
        <div className="bleed-inner bleed-headroom relative py-6 sm:py-8">
          {/* The same top line, drawn properly. "All events" goes where the
              widget was configured to send it and the two buttons are shapes
              the page already knows it needs, so nothing on this row moves
              when the event lands. */}
          <div className="mb-6 flex items-center gap-2">
            <a
              href={finderUrl}
              className="kicker inline-flex w-fit items-center gap-1 transition-[filter] hover:brightness-110"
            >
              <ChevronLeft className="h-3 w-3" /> All events
            </a>
            <div className="ml-auto flex items-center gap-1.5" aria-hidden>
              <span className={`${HERO_ICON} opacity-50`}>
                <CalendarPlus className="h-4 w-4" />
              </span>
              <span className={`${HERO_ICON} opacity-50`}>
                <Share className="h-4 w-4" />
              </span>
            </div>
          </div>
          <div className="grid gap-6 sm:gap-8 lg:grid-cols-[26rem_1fr]">
            {/* Not an empty box: the church's mark, quietly, in the frame the
                poster is about to fill. */}
            <div className="ring-opacity-15 flex aspect-video w-full items-center justify-center rounded-2xl bg-white/5 ring-1 ring-white/10">
              <WoodsideLogo tone="current" className="h-14 w-14 text-white/20" />
            </div>
            <div className="min-w-0 self-center">
              {/* WHAT, WHEN and WHERE are the page's own words, not the
                  event's — every event has all three and no database was ever
                  going to be asked about them. They are set from the first
                  frame, and only the answers are waiting. */}
              <Fact tone="hero" icon={<Info className="h-5 w-5 opacity-70" />} label="What">
                <div className="space-y-2 pt-1">
                  <Bar art w="100%" />
                  <Bar art w="92%" />
                  <Bar art w="64%" />
                </div>
              </Fact>
              <div className="mt-2">
                <Fact tone="hero" icon={<Clock className="h-5 w-5 opacity-70" />} label="When">
                  <div className="space-y-2 pt-1">
                    <Bar art w="11rem" />
                    <Bar art w="7rem" />
                  </div>
                </Fact>
                <Fact tone="hero" icon={<MapPin className="h-5 w-5 opacity-70" />} label="Where">
                  <div className="space-y-2 pt-1">
                    <Bar art w="9rem" />
                    <Bar art w="14rem" />
                  </div>
                </Fact>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-4">
          {/* The facts card, with its own three labels already said. */}
          <section className="card divide-line divide-y p-0">
            <div className="p-4">
              <Fact icon={<Church className="text-teal h-5 w-5" />} label="Campus">
                <Bar w="8rem" />
              </Fact>
            </div>
            <div className="p-4">
              <Fact icon={<Users className="text-teal h-5 w-5" />} label="Ministry">
                <Bar w="6rem" />
              </Fact>
            </div>
            <div className="p-4">
              <Fact
                icon={<MessageCircleQuestion className="text-teal h-5 w-5" />}
                label="Questions"
              >
                <div className="flex items-center gap-3 pt-1">
                  <div className="skeleton h-9 w-9 shrink-0 rounded-full" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <Bar w="7rem" />
                    <Bar w="12rem" />
                  </div>
                </div>
              </Fact>
            </div>
          </section>
          <div className="skeleton h-4 w-full" />
          <div className="skeleton h-4 w-5/6" />
          <div className="skeleton h-4 w-2/3" />
        </div>
        <div className="card space-y-3 p-4">
          <p className="kicker">Who</p>
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 py-1">
              <div className="skeleton h-5 w-5 shrink-0 rounded-md" />
              <div className="skeleton h-9 w-9 shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 space-y-2">
                <Bar w="8rem" />
                <Bar w="5rem" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/** The chrome both hero buttons wear: a circle on the art, not a pill. */
const HERO_ICON =
  'flex h-9 w-9 items-center justify-center rounded-full bg-white/15 backdrop-blur transition hover:bg-white/25';

function ShareButton({ title, url }: { title: string; url: string }) {
  const [done, setDone] = useState(false);
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    } catch {
      /* dismissed */
    }
  };
  return (
    <button
      type="button"
      onClick={share}
      className={HERO_ICON}
      aria-label={done ? 'Link copied' : 'Share this event'}
      title={done ? 'Link copied' : 'Share'}
    >
      {done ? <Check className="h-4 w-4" /> : <Share className="h-4 w-4" />}
    </button>
  );
}

/** Closes a popover on an outside pointer or Escape. Shared by both menus. */
function useDismiss(open: boolean, wrap: React.RefObject<HTMLElement | null>, close: () => void) {
  useEffect(() => {
    if (!open) return;
    // composedPath, not contains: inside a shadow root the event target the
    // document sees is the host, so `contains` would close on every click.
    const onPointer = (e: Event) => {
      if (wrap.current && !e.composedPath().includes(wrap.current)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, wrap, close]);
}

/**
 * WHERE IT IS, AS A PLACE RATHER THAN A MENU
 *
 * The row is the trigger, and tapping it opens `WhereSheet` — a map, the
 * address, and the same three things to do with it. It used to drop a little
 * menu of three apps, which is an answer to "how do I get there" from a page
 * that never showed anybody where "there" is. See WhereSheet.tsx.
 */
function WhereFact({
  name,
  address,
  lat,
  lng,
  accent,
  children,
  online,
  tone = 'card',
}: {
  /** The venue MP names, for the sheet's heading. */
  name: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
  accent?: string;
  children: React.ReactNode;
  /** The join link, which cannot live inside the row's button. */
  online?: React.ReactNode;
  tone?: 'card' | 'hero';
}) {
  const [open, setOpen] = useState(false);
  const icon = <MapPin className={`h-5 w-5 ${tone === 'hero' ? 'opacity-70' : 'text-teal'}`} />;

  // Nowhere to send anybody: the row is just a row.
  if (!address)
    return (
      <Fact icon={icon} label="Where" tone={tone}>
        {children}
        {online}
      </Fact>
    );

  return (
    <>
      <Fact
        icon={icon}
        label="Where"
        tone={tone}
        onClick={() => setOpen(true)}
        expanded={open}
        after={online}
      >
        {children}
      </Fact>
      <WhereSheet
        open={open}
        onClose={() => setOpen(false)}
        name={name}
        address={address}
        lat={lat}
        lng={lng}
        accent={accent}
      />
    </>
  );
}

function AddToCalendar({ icsHref, googleHref }: { icsHref: string; googleHref: string }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useDismiss(
    open,
    wrap,
    useCallback(() => setOpen(false), [])
  );
  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        className={HERO_ICON}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Add to calendar"
        title="Add to calendar"
      >
        <CalendarPlus className="h-4 w-4" />
      </button>
      {open && (
        <div className="glass rise absolute top-full right-0 z-20 mt-2 w-52 overflow-hidden rounded-2xl p-1.5">
          <a
            href={icsHref}
            className="text-navy block rounded-xl px-3 py-2 text-sm font-semibold hover:bg-white"
            onClick={() => setOpen(false)}
          >
            Apple, Outlook (.ics)
          </a>
          <a
            href={googleHref}
            target="_blank"
            rel="noopener"
            className="text-navy block rounded-xl px-3 py-2 text-sm font-semibold hover:bg-white"
            onClick={() => setOpen(false)}
          >
            Google Calendar
          </a>
        </div>
      )}
    </div>
  );
}
